/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.sprudel.pattern

import io.peekandpoke.klang.common.math.CycleTime

import io.peekandpoke.klang.sprudel.SprudelPattern
import io.peekandpoke.klang.sprudel.SprudelPattern.QueryContext
import io.peekandpoke.klang.sprudel.SprudelPatternEvent

/**
 * Generic pattern that applies a transformation to events after querying the source.
 *
 * This is a reusable wrapper for simple event transformations (filtering, mapping, etc.)
 * that preserves the pattern's structure and delegates all properties to the source.
 *
 * This eliminates the need for specific pattern classes that just apply simple transformations.
 *
 * @param source The source pattern to query
 * @param transform Function that transforms the list of events after querying
 */
internal class MapPattern(
    private val source: SprudelPattern,
    private val transform: (List<SprudelPatternEvent>) -> List<SprudelPatternEvent>,
) : SprudelPattern {

    override val weight: Double get() = source.weight
    override val numSteps: Double? get() = source.numSteps
    override fun estimateCycleDuration(): Double = source.estimateCycleDuration()

    override fun queryArcContextual(
        from: CycleTime,
        to: CycleTime,
        ctx: QueryContext,
    ): List<SprudelPatternEvent> {
        return transform(source.queryArcContextual(from, to, ctx))
    }
}
